import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { DeliveryIcon } from "@/shared/icons/DeliveryIcon";
import { Checkbox } from "@/shared/ui";
import CartPayButtons from "@/shared/widgets/CartPayButtons";

type Props = {
  totalCount: number;
  totalPrice: number;
  onPay: () => void;
  isPaying: boolean;
  handleClear: () => void;
  deliveryType: "default" | "express";
  setDeliveryType: () => void;
};

export default function GoodsAndWarehouseCartFooter({
  totalCount,
  totalPrice,
  onPay,
  isPaying,
  handleClear,
  deliveryType,
  setDeliveryType,
}: Props) {
  const { t } = useTranslation("business");

  const priceWithDelivery = deliveryType === "express" ? totalPrice + 1000 : totalPrice;

  return (
    <div className="flex flex-col gap-5 w-full mt-5">
      <div className="flex items-center px-5 py-3.5 bg-[linear-gradient(0deg,#141414_0%,#141414_100%),linear-gradient(90deg,#191919_0%,#141414_50%)] border border-[#191919] rounded-[0.75rem]">
        <DeliveryIcon className="size-5" />
        <p className="ml-2.5 text-15 font-bold leading-none">
          {t("business.goodsAndWarehouse.chooseDeliveryType")}:
        </p>
        <div className="flex items-center gap-2.5 ml-auto">
          <Checkbox checked={deliveryType === "default"} onChange={setDeliveryType} />
          <div>
            <p className="mb-1 text-15 font-bold leading-none">
              {t("business.goodsAndWarehouse.usual")}
              <span className="inline-block ml-2 text-14 font-medium text-white/70 leading-none">
                24 {t("business.goodsAndWarehouse.hour2")}
              </span>
            </p>
            <p className="text-14 font-bold text-accent-solid leading-none">0 ₽</p>
          </div>
        </div>
        <div className="w-px h-5.5 mx-5 bg-dark-800" />
        <div className="flex items-center gap-2.5">
          <Checkbox checked={deliveryType === "express"} onChange={setDeliveryType} />
          <div>
            <p className="mb-1 text-15 font-bold leading-none">
              {t("business.goodsAndWarehouse.express")}
              <span className="inline-block ml-2 text-14 font-medium text-white/70 leading-none">
                2 {t("business.goodsAndWarehouse.hour2")}
              </span>
            </p>
            <p className="text-14 font-bold text-accent-solid leading-none">
              {formatPrice(1000)} ₽
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center">
        <div>
          <p className="mb-1 text-18 font-bold leading-none">{formatPrice(totalPrice)} ₽</p>
          <p className="text-15 font-medium text-white/50 leading-none">
            {t("business.goodsAndWarehouse.cartPrice")}
          </p>
        </div>
        <div className="w-px h-5.5 mx-5 bg-dark-800" />
        <div>
          <p className="mb-1 text-18 font-bold leading-none">{totalCount || 0}</p>
          <p className="text-15 font-medium text-white/50 leading-none">
            {t("business.goodsAndWarehouse.totalGoods")}
          </p>
        </div>
        <div className="ml-auto text-right">
          <p className="mb-1 text-18 font-bold text-accent-solid leading-none">
            {formatPrice(priceWithDelivery)} ₽
          </p>
          <p className="text-15 font-medium leading-none">
            {t("business.goodsAndWarehouse.priceWithDelivery")}
          </p>
        </div>
      </div>
      <CartPayButtons
        i18FileName="business"
        onPay={onPay}
        isPaying={isPaying}
        handleClear={handleClear}
      />
    </div>
  );
}
